import { AxiosInstance, AxiosResponse } from "axios";
import { fetcher } from "../../configurations";
import {
    ApproveLoteRequest,
    Armador,
    BundleAssignmentDashboard,
    CreateLoteRequest,
    SubmitRevisionRequest,
} from "../../interfaces";
import { bundleAssignmentMock } from "./bundle-assignment.repository.mock";

const useMockCreate = process.env.REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK_CREATE === "true";
const useMockVerify = process.env.REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK_VERIFY === "true";
const useMockMutations = process.env.REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK !== "false";

let cachedDashboard: BundleAssignmentDashboard | null = null;

/**
 * Bundle Assignment API
 *
 * getDashboard always uses lm-api (REACT_APP_URL_API_2).
 * createLote uses real HTTP by default (REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK_CREATE=true for mock).
 * approveLote (verificación) uses real HTTP by default (REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK_VERIFY=true for mock).
 * submitRevision still mock unless REACT_APP_BUNDLE_ASSIGNMENT_USE_MOCK=false.
 *
 * GET /bundle-assignment/dashboard
 * Response: { inventario: Bota[], lotes: Lote[], armadores: string[], revisiones: RevisionesMap }
 *
 * POST /bundle-assignment/lotes
 * Body: { botaIds: string[], armador: string }
 * Response: updated dashboard
 *
 * POST /bundle-assignment/lotes/:id/revision
 * Body: { botaId: string, resultado: ResultadoRevision }
 * Response: updated dashboard
 *
 * PUT /bundle-assignment/lotes/:id/verificacion
 * Body: { revisiones: Record<string, "OK" | "Pendiente"> }
 * Response: updated dashboard
 *
 * PUT /bundle-assignment/lotes/:id/aprobar (alias)
 * Body: { revisiones: Record<string, "OK" | "Pendiente"> }
 * Response: updated dashboard
 */
export class BundleAssignmentRepository {
    private lmBootApi: AxiosInstance;

    constructor() {
        this.lmBootApi = fetcher(process.env.REACT_APP_URL_API_2 as string);
    }

    async getDashboard(): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        const response = await this.lmBootApi.get<BundleAssignmentDashboard>("/bundle-assignment/dashboard");
        cachedDashboard = response.data;
        return response;
    }

    createLote(payload: CreateLoteRequest): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        if (useMockCreate) {
            return bundleAssignmentMock.createLote(payload, cachedDashboard).then((response) => {
                cachedDashboard = response.data;
                return response;
            });
        }
        return this.lmBootApi.post<BundleAssignmentDashboard>("/bundle-assignment/lotes", payload).then((response) => {
            cachedDashboard = response.data;
            return response;
        });
    }

    submitRevision(
        loteId: string,
        payload: SubmitRevisionRequest
    ): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        if (useMockMutations) {
            return bundleAssignmentMock.submitRevision(loteId, payload);
        }
        return this.lmBootApi.post<BundleAssignmentDashboard>(
            `/bundle-assignment/lotes/${loteId}/revision`,
            payload
        );
    }

    approveLote(
        loteId: string,
        payload: ApproveLoteRequest
    ): Promise<AxiosResponse<BundleAssignmentDashboard>> {
        if (useMockVerify) {
            return bundleAssignmentMock.approveLote(loteId, payload).then((response) => {
                cachedDashboard = response.data;
                return response;
            });
        }
        return this.lmBootApi.put<BundleAssignmentDashboard>(
            `/bundle-assignment/lotes/${loteId}/verificacion`,
            payload
        ).then((response) => {
            cachedDashboard = response.data;
            return response;
        });
    }
}
